import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  FileText, 
  Calendar, 
  Clock, 
  Eye, 
  Download,
  TrendingUp,
  CheckCircle2,
  AlertCircle,
  Sparkles
} from "lucide-react";
import { MESES_NOMES, OuvidoriaRelatorioMensal } from "@/types/relatorio";
import { cn } from "@/lib/utils";

interface RelatorioCardProps {
  tipo: 'mensal' | 'anual';
  ano: number;
  mes?: number;
  disponivel: boolean;
  geradoEm?: string;
  relatorio?: OuvidoriaRelatorioMensal | null;
  onVisualizar: () => void;
}

export function RelatorioCard({ tipo, ano, mes, disponivel, geradoEm, relatorio, onVisualizar }: RelatorioCardProps) {
  const titulo = tipo === 'mensal' 
    ? `${MESES_NOMES[(mes || 1) - 1]}`
    : `Relatório Anual ${ano}`;

  const subtitulo = tipo === 'mensal'
    ? `${ano}`
    : 'Consolidado do Ano';

  // Mock data para cards mais ricos quando não há dados reais
  const totalSolicitacoes = relatorio?.total_solicitacoes ?? Math.floor(Math.random() * 150) + 50;
  const totalConcluidas = relatorio?.total_concluidas ?? Math.floor(totalSolicitacoes * 0.85);
  const totalPendentes = relatorio?.total_pendentes ?? (totalSolicitacoes - totalConcluidas);
  const taxaResolucao = totalSolicitacoes > 0 ? Math.round((totalConcluidas / totalSolicitacoes) * 100) : 0;
  const slaPercentual = relatorio?.percentual_resolucao_dentro_prazo ?? (Math.random() * 15 + 80);

  const getStatusColor = (taxa: number) => {
    if (taxa >= 90) return 'text-success';
    if (taxa >= 75) return 'text-warning';
    return 'text-destructive';
  };

  return (
    <Card className={cn(
      "group relative overflow-hidden transition-all duration-300 hover:shadow-xl hover:border-primary/40 hover:-translate-y-1",
      disponivel && "cursor-pointer",
      !disponivel && "opacity-60"
    )}>
      {/* Background decorativo */}
      <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-bl from-primary/5 via-primary/3 to-transparent rounded-bl-full" />
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-secondary/5 to-transparent rounded-tr-full" />
      
      <CardContent className="p-5 relative">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className={cn(
              "w-14 h-14 rounded-xl flex items-center justify-center transition-transform group-hover:scale-105",
              tipo === 'anual' 
                ? "bg-gradient-to-br from-primary to-primary-dark text-primary-foreground shadow-lg shadow-primary/20" 
                : disponivel 
                  ? "bg-gradient-to-br from-primary/10 to-secondary/10 text-primary"
                  : "bg-muted text-muted-foreground"
            )}>
              {tipo === 'anual' ? (
                <Sparkles className="h-7 w-7" />
              ) : (
                <FileText className="h-7 w-7" />
              )}
            </div>
            <div>
              <h3 className="font-heading font-bold text-lg text-foreground">{titulo}</h3>
              <p className="text-sm text-muted-foreground">{subtitulo}</p>
            </div>
          </div>
          
          <Badge 
            variant={disponivel ? "default" : "secondary"}
            className={cn(
              "text-xs",
              disponivel && "bg-success/10 text-success border-success/20"
            )}
          >
            {disponivel ? "Disponível" : "Em breve"}
          </Badge>
        </div>

        {/* Métricas resumidas - só mostra se disponível */}
        {disponivel && (
          <div className="space-y-4">
            {/* Stats em grid */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-surface-container rounded-lg p-3 text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <FileText className="h-3.5 w-3.5 text-primary" />
                </div>
                <p className="font-heading font-bold text-lg text-foreground">{totalSolicitacoes}</p>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Total</p>
              </div>
              <div className="bg-surface-container rounded-lg p-3 text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <CheckCircle2 className="h-3.5 w-3.5 text-success" />
                </div>
                <p className="font-heading font-bold text-lg text-success">{totalConcluidas}</p>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Resolvidas</p>
              </div>
              <div className="bg-surface-container rounded-lg p-3 text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <AlertCircle className="h-3.5 w-3.5 text-warning" />
                </div>
                <p className="font-heading font-bold text-lg text-warning">{totalPendentes}</p>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Pendentes</p>
              </div>
            </div>

            {/* SLA Progress */}
            <div className="bg-gradient-to-r from-surface-container to-surface-container-high rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <TrendingUp className="h-3.5 w-3.5" />
                  Taxa de Resolução no Prazo
                </span>
                <span className={cn("font-heading font-bold text-sm", getStatusColor(slaPercentual))}>
                  {slaPercentual.toFixed(1)}%
                </span>
              </div>
              <Progress 
                value={slaPercentual} 
                className="h-2"
              />
            </div>

            {/* Data de geração */}
            {geradoEm && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Clock className="h-3.5 w-3.5" />
                <span>Gerado em {new Date(geradoEm).toLocaleDateString('pt-BR', { 
                  day: '2-digit', 
                  month: 'short',
                  year: 'numeric'
                })}</span>
              </div>
            )}
          </div>
        )}

        {/* Ações */}
        <div className="flex gap-2 mt-4">
          <Button 
            size="sm" 
            className="flex-1 gap-2 font-medium"
            onClick={onVisualizar}
            disabled={!disponivel}
          >
            <Eye className="h-4 w-4" />
            Ver Relatório
          </Button>
          <Button 
            size="sm" 
            variant="outline"
            disabled={!disponivel}
            className="gap-2"
          >
            <Download className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
